import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../api.js';
import { useMe } from '../auth.js';
import { Button, ErrorText, Input } from '../components/ui.js';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'viewer';
  totpEnabled: boolean;
  disabled: boolean;
  lockedUntil: string | null;
}

export function UsersPage() {
  const me = useMe();
  const client = useQueryClient();
  const users = useQuery({ queryKey: ['users'], queryFn: () => api<User[]>('GET', '/api/users') });
  const reload = () => client.invalidateQueries({ queryKey: ['users'] });
  const isAdmin = me.user?.role === 'admin';

  const action = useMutation({
    mutationFn: ({ method, url, body }: { method: string; url: string; body?: unknown }) => api(method, url, body),
    onSuccess: reload,
  });

  const resetPassword = (u: User) => {
    const password = window.prompt(`New password for ${u.email} (at least 12 characters)`);
    if (password) action.mutate({ method: 'POST', url: `/api/users/${u.id}/reset-password`, body: { password } });
  };

  return (
    <section className="space-y-6">
      <h1 className="text-xl font-semibold">Users</h1>
      {users.isError && <ErrorText>Could not load users.</ErrorText>}
      {action.isError && <ErrorText>{action.error instanceof ApiError ? action.error.message : 'Action failed'}</ErrorText>}
      <table className="w-full text-left text-sm">
        <thead className="text-slate-500">
          <tr>
            <th className="py-1">Email</th>
            <th>Name</th>
            <th>Role</th>
            <th>2FA</th>
            <th>State</th>
            {isAdmin && <th />}
          </tr>
        </thead>
        <tbody>
          {users.data?.map((u) => (
            <tr key={u.id} className="border-t border-slate-200 dark:border-slate-800">
              <td className="py-1.5">{u.email}</td>
              <td>{u.name}</td>
              <td>{u.role}</td>
              <td>{u.totpEnabled ? 'on' : 'pending'}</td>
              <td>{u.disabled ? 'disabled' : u.lockedUntil && new Date(u.lockedUntil) > new Date() ? 'locked' : 'active'}</td>
              {isAdmin && (
                <td className="space-x-2 text-right">
                  {u.id !== me.user?.id && (
                    <>
                      <button className="underline" onClick={() => action.mutate({ method: 'PATCH', url: `/api/users/${u.id}`, body: { role: u.role === 'admin' ? 'viewer' : 'admin' } })}>
                        make {u.role === 'admin' ? 'viewer' : 'admin'}
                      </button>
                      <button className="underline" onClick={() => action.mutate({ method: 'PATCH', url: `/api/users/${u.id}`, body: { disabled: !u.disabled } })}>
                        {u.disabled ? 'enable' : 'disable'}
                      </button>
                    </>
                  )}
                  <button className="underline" onClick={() => resetPassword(u)}>
                    reset password
                  </button>
                  <button className="underline" onClick={() => action.mutate({ method: 'POST', url: `/api/users/${u.id}/reset-totp` })}>
                    reset 2FA
                  </button>
                  {u.id !== me.user?.id && (
                    <button
                      className="underline text-red-700 dark:text-red-400"
                      onClick={() => window.confirm(`Delete ${u.email}?`) && action.mutate({ method: 'DELETE', url: `/api/users/${u.id}` })}
                    >
                      delete
                    </button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {isAdmin && <CreateUser onCreated={reload} />}
    </section>
  );
}

function CreateUser({ onCreated }: { onCreated: () => void }) {
  const [form, setForm] = useState({ email: '', name: '', role: 'viewer', password: '' });
  const create = useMutation({
    mutationFn: () => api('POST', '/api/users', form),
    onSuccess: () => {
      setForm({ email: '', name: '', role: 'viewer', password: '' });
      onCreated();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  return (
    <form onSubmit={submit} className="grid max-w-xl grid-cols-2 gap-3">
      <h2 className="col-span-2 font-semibold">New user</h2>
      <Input label="Email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
      <Input label="Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      <label className="block text-sm">
        <span className="mb-1 block text-slate-600 dark:text-slate-400">Role</span>
        <select className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 dark:border-slate-700 dark:bg-slate-900" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
          <option value="viewer">viewer</option>
          <option value="admin">admin</option>
        </select>
      </label>
      <Input label="Initial password (min. 12)" type="password" required minLength={12} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
      {create.isError && <div className="col-span-2"><ErrorText>{create.error instanceof ApiError ? create.error.message : 'Could not create the user'}</ErrorText></div>}
      <div className="col-span-2">
        <Button type="submit" disabled={create.isPending}>Create user</Button>
      </div>
    </form>
  );
}
